import { onBeforeUnmount, watch, type Ref } from 'vue';

/**
 * Следит за реальной высотой элемента и отдаёт её колбэком.
 *
 * Нужна тем блокам, чью высоту нельзя посчитать заранее, а соседям — надо:
 * панели ввода чата (растёт вместе с textarea до 5 строк, лента должна ровно
 * на неё отступать — QA AVIATA-3712 п.9) и нижней панели с ценой и кнопкой
 * (`PriceAndButtonCard`: кешбэк, рассрочка и согласие факторинга появляются по
 * условию, а страница обязана зарезервировать под панель ровно её высоту).
 *
 * `ResizeObserver` есть во всех вебвью, где живёт мини-апп; там, где его нет
 * (jsdom в юнитах), просто ничего не наблюдаем — отступ остаётся дефолтным.
 */
export function useElementHeight(
  target: Ref<HTMLElement | null>,
  onHeight: (height: number) => void,
): void {
  let observer: ResizeObserver | null = null;

  const stopObserving = (): void => {
    observer?.disconnect();
    observer = null;
  };

  watch(
    target,
    element => {
      stopObserving();
      if (!element || typeof ResizeObserver === 'undefined') {
        return;
      }
      onHeight(element.getBoundingClientRect().height);
      observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          onHeight(entry.contentRect.height + borderBoxExtra(entry));
        }
      });
      observer.observe(element);
    },
    { immediate: true },
  );

  onBeforeUnmount(stopObserving);
}

// contentRect не включает паддинги/бордеры, а панели ввода они нужны — берём
// разницу из borderBoxSize, когда браузер её отдаёт.
const borderBoxExtra = (entry: ResizeObserverEntry): number => {
  // lib.dom описывает borderBoxSize только массивом, но ранние реализации
  // отдавали одиночный объект, а совсем старые не отдавали ничего — поэтому
  // разбираем все три случая и сужаем от unknown.
  const sizes: unknown = entry.borderBoxSize;
  const borderBox = (Array.isArray(sizes) ? sizes[0] : sizes) as
    | ResizeObserverSize
    | undefined;
  if (!borderBox) {
    return 0;
  }
  return Math.max(0, borderBox.blockSize - entry.contentRect.height);
};
